import React, { useState, useEffect, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Phone, Voicemail, PhoneCall, PhoneIncoming, PhoneOutgoing,
  Settings, Users, Wifi, WifiOff, Loader2, AlertCircle,
  ClipboardList, StickyNote, MessageSquare, X, User, FileText,
  Briefcase, Clock, DollarSign, Calendar, Search, RefreshCw
} from 'lucide-react';
import Softphone from './Softphone';
import PhoneConsole from './PhoneConsole';
import CallLog from './CallLog';
import VoicemailList from './VoicemailList';
import SpeedDials from './SpeedDials';
import VoipSettings from './VoipSettings';
import QuickIntakeModal from '../intake/QuickIntakeModal';
import { voipService } from '../../services/voipService';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import api from '../../services/api';

// Error Boundary to catch and display errors instead of blank screen
class VoipErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('VoipPage Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
          <div className="text-center max-w-lg p-6 bg-virgo-navy-800 rounded-xl border border-red-500/30">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Phone Error</h2>
            <p className="text-virgo-teal-400 mb-4">{this.state.error?.message || 'Something went wrong'}</p>
            <pre className="text-xs text-left bg-virgo-navy-900 p-3 rounded mb-4 overflow-auto max-h-32 text-red-300">
              {this.state.error?.stack?.slice(0, 500)}
            </pre>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-cyan-500 text-white rounded-lg flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const VoipPage = () => {
  const [activeTab, setActiveTab] = useState('phone');
  const [extension, setExtension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [rcStatus, setRcStatus] = useState(null);

  // Side panel modals
  const [showQuickIntakeModal, setShowQuickIntakeModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [intakePreFill, setIntakePreFill] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open QuickIntakeModal if navigated with ?intake=1
  useEffect(() => {
    const intakeParam = searchParams.get('intake');
    const phoneParam = searchParams.get('phone');
    if (intakeParam === '1') {
      if (phoneParam) {
        setIntakePreFill({ primary_phone: decodeURIComponent(phoneParam) });
      }
      setShowQuickIntakeModal(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  // Lifted phone state (shared between Softphone and PhoneConsole)
  const [phoneNumber, setPhoneNumber] = useState(() => {
    // Check for pending dial number from Android deep link or Astraea
    const pending = sessionStorage.getItem('virgo_dial_number');
    if (pending) {
      sessionStorage.removeItem('virgo_dial_number');
      return pending;
    }
    return '';
  });
  const [callState, setCallState] = useState('idle'); // idle, calling, ringing, incoming, active, hold
  const [connectionState, setConnectionState] = useState('disconnected');
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);

  // Call notes state - persist to localStorage to survive crashes/remounts
  const [callNotes, setCallNotes] = useState(() => {
    try { return localStorage.getItem('virgo_call_notes') || ''; } catch { return ''; }
  });
  const [callNoteCase, setCallNoteCase] = useState(() => {
    try { return localStorage.getItem('virgo_call_note_case') || ''; } catch { return ''; }
  });
  const [callNoteBillable, setCallNoteBillable] = useState(() => {
    try { return localStorage.getItem('virgo_call_note_billable') === 'true'; } catch { return false; }
  });
  const [callDuration, setCallDuration] = useState(0);
  const [callStartTime, setCallStartTime] = useState(null);
  const [availableCases, setAvailableCases] = useState([]);

  // Listen for dial-number events from Android bridge or Astraea
  useEffect(() => {
    const handleDialNumber = (e) => {
      const number = e.detail?.number;
      const autoDial = e.detail?.autoDial;
      if (number) {
        console.log('[VoipPage] Received dial-number event:', number, 'autoDial:', autoDial);
        setPhoneNumber(number);
        setActiveTab('phone');

        // If auto-dial was requested (from Astraea voice command), click the dial button
        if (autoDial) {
          setTimeout(() => {
            const dialBtn = document.querySelector('[data-dial-button]');
            if (dialBtn) {
              console.log('[VoipPage] Auto-clicking dial button');
              dialBtn.click();
            } else {
              console.warn('[VoipPage] Dial button not found for auto-dial');
            }
          }, 1500);
        }
      }
    };

    // Check for auto-dial on mount (from sessionStorage flag)
    const pendingAutoDial = sessionStorage.getItem('virgo_auto_dial');
    if (pendingAutoDial) {
      sessionStorage.removeItem('virgo_auto_dial');
      const number = sessionStorage.getItem('virgo_dial_number');
      if (number) {
        console.log('[VoipPage] Auto-dial on mount:', number);
        setActiveTab('phone');
        setTimeout(() => {
          const dialBtn = document.querySelector('[data-dial-button]');
          if (dialBtn) {
            console.log('[VoipPage] Auto-clicking dial button on mount');
            dialBtn.click();
          }
        }, 2000);
      }
    }

    window.addEventListener('virgo-dial-number', handleDialNumber);
    return () => window.removeEventListener('virgo-dial-number', handleDialNumber);
  }, []);

  // Persist call notes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('virgo_call_notes', callNotes);
      localStorage.setItem('virgo_call_note_case', callNoteCase);
      localStorage.setItem('virgo_call_note_billable', callNoteBillable ? 'true' : 'false');
    } catch (e) {
      console.warn('Failed to persist call notes:', e);
    }
  }, [callNotes, callNoteCase, callNoteBillable]);

  useEffect(() => {
    loadExtension();
    loadStatistics();
    checkRingCentralStatus();
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const response = await api.get('/cases/?status=active&limit=100');
      // Handle various response formats - ensure we always get an array
      let cases = [];
      if (Array.isArray(response.data)) {
        cases = response.data;
      } else if (response.data && Array.isArray(response.data.items)) {
        cases = response.data.items;
      } else if (response.data && Array.isArray(response.data.cases)) {
        cases = response.data.cases;
      }
      setAvailableCases(cases);
    } catch (error) {
      console.error('Error loading cases:', error);
      setAvailableCases([]);
    }
  };

  // Clear call notes after saving (call this when call ends and notes are filed)
  const clearCallNotes = () => {
    setCallNotes('');
    setCallNoteCase('');
    setCallNoteBillable(false);
    try {
      localStorage.removeItem('virgo_call_notes');
      localStorage.removeItem('virgo_call_note_case');
      localStorage.removeItem('virgo_call_note_billable');
    } catch (e) {
      console.warn('Failed to clear call notes from localStorage:', e);
    }
  };

  // Safe modal opener that won't crash
  const safeOpenModal = (setter) => {
    try {
      setter(true);
    } catch (e) {
      console.error('Error opening modal:', e);
    }
  };

  const loadExtension = async () => {
    try {
      const ext = await voipService.getMyExtension();
      setExtension(ext);
    } catch (error) {
      console.error('Error loading extension:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await voipService.getCallStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const checkRingCentralStatus = async () => {
    try {
      const response = await api.get('/ringcentral/webrtc/status');
      setRcStatus(response.data);
    } catch (error) {
      console.error('Error checking RingCentral status:', error);
      setRcStatus({ ready: false, message: 'Failed to check status' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-400 mx-auto" />
          <p className="mt-4 text-virgo-teal-400">Loading Planchette...</p>
        </div>
      </div>
    );
  }

  if (!extension) {
    return (
      <div className="flex items-center justify-center h-screen bg-virgo-navy-900">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 flex items-center justify-center">
            <Phone className="w-10 h-10 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">No Extension Configured</h2>
          <p className="text-virgo-teal-400 mb-4">
            You don't have a phone extension set up yet. Please contact your administrator to configure your extension.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-virgo-navy-900">
      {/* Header */}
      <div className="bg-virgo-navy-800 border-b border-virgo-navy-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-virgo-gold-400 to-amber-300 bg-clip-text text-transparent">
                Planchette
              </h1>
              <p className="text-sm text-virgo-teal-400">
                Extension {extension.extension} - {extension.display_name}
              </p>
            </div>
          </div>

          {/* Connection Status */}
          <div className="flex items-center gap-6">
            {/* RingCentral Status */}
            <div className="flex items-center gap-2 px-4 py-2 bg-virgo-navy-700 rounded-lg border border-virgo-navy-600">
              {rcStatus?.ready ? (
                <>
                  <Wifi className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400">
                    {rcStatus?.message || 'Checking...'}
                  </span>
                </>
              )}
            </div>

            {/* Statistics */}
            {statistics && (
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{statistics.total_calls || 0}</div>
                  <div className="text-xs text-virgo-teal-500">Total Calls</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">{statistics.answered_calls || 0}</div>
                  <div className="text-xs text-virgo-teal-500">Answered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">{statistics.missed_calls || 0}</div>
                  <div className="text-xs text-virgo-teal-500">Missed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">{statistics.astraea_handled_calls || 0}</div>
                  <div className="text-xs text-virgo-teal-500">Astraea</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-virgo-navy-800 border-b border-virgo-navy-700">
        <nav className="flex space-x-1 px-6">
          {[
            { id: 'phone', icon: Phone, label: 'Softphone' },
            { id: 'calls', icon: PhoneCall, label: 'Call Log' },
            { id: 'voicemail', icon: Voicemail, label: 'Voicemail' },
            { id: 'speed-dials', icon: Users, label: 'Speed Dials' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-virgo-teal-400 hover:text-white hover:border-virgo-navy-500'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'phone' && (
          <div className="flex gap-6 justify-center">
            {/* Phone Console - Left Panel */}
            <PhoneConsole
              currentExtension={extension}
              callState={callState}
              connectionState={connectionState}
              activeCall={activeCall}
              onDialNumber={(num) => setPhoneNumber(num)}
            />

            {/* Softphone - Center */}
            <Softphone
              extension={extension}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              callState={callState}
              setCallState={setCallState}
              connectionState={connectionState}
              setConnectionState={setConnectionState}
              activeCall={activeCall}
              setActiveCall={setActiveCall}
              incomingCallInfo={incomingCallInfo}
              setIncomingCallInfo={setIncomingCallInfo}
            />

            {/* Quick Actions - Right Panel */}
            <div className="w-72 space-y-4">
              {/* Quick Actions Card */}
              <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 overflow-hidden">
                <div className="px-4 py-3 border-b border-virgo-navy-600">
                  <h3 className="text-sm font-semibold text-virgo-gold-400">Quick Actions</h3>
                </div>
                <div className="p-3 space-y-2">
                  {/* Quick Intake Button */}
                  <button
                    onClick={() => safeOpenModal(setShowQuickIntakeModal)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 hover:from-teal-500/30 hover:to-cyan-500/30 border border-teal-500/30 rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center group-hover:bg-teal-500/30 transition-colors">
                      <ClipboardList className="w-5 h-5 text-teal-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-white">Quick Intake</div>
                      <div className="text-xs text-teal-400">Create new prospect</div>
                    </div>
                  </button>

                  {/* Client Notes Button */}
                  <button
                    onClick={() => safeOpenModal(setShowNotesModal)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 hover:from-amber-500/30 hover:to-yellow-500/30 border border-amber-500/30 rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                      <StickyNote className="w-5 h-5 text-amber-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-white">Call Notes</div>
                      <div className="text-xs text-amber-400">Add notes during call</div>
                    </div>
                  </button>

                  {/* Messages Button */}
                  <button
                    onClick={() => safeOpenModal(setShowMessagesModal)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 hover:from-cyan-500/30 hover:to-indigo-500/30 border border-cyan-500/30 rounded-xl transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                      <MessageSquare className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-medium text-white">Messages</div>
                      <div className="text-xs text-cyan-400">View/send SMS</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Call Notes Preview (if notes exist) */}
              {callNotes && (
                <div className="bg-virgo-navy-800 rounded-xl border border-virgo-navy-600 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-amber-400 flex items-center gap-2">
                      <StickyNote className="w-4 h-4" />
                      Call Notes
                    </h4>
                    <button
                      onClick={() => setShowNotesModal(true)}
                      className="text-xs text-virgo-teal-400 hover:text-white"
                    >
                      Edit
                    </button>
                  </div>
                  {/* Status badges */}
                  <div className="flex gap-2 mb-2">
                    {callNoteBillable && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Billable
                      </span>
                    )}
                    {callNoteCase && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        Filed
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-virgo-teal-300 line-clamp-3">{callNotes}</p>
                </div>
              )}

              {/* Tips Card */}
              <div className="bg-virgo-navy-800/50 rounded-xl border border-virgo-navy-600 p-4">
                <h4 className="text-sm font-medium text-virgo-teal-400 mb-2">Tips</h4>
                <ul className="text-xs text-virgo-teal-500 space-y-1">
                  <li>• Create intake for new callers</li>
                  <li>• Add notes during the call</li>
                  <li>• Send follow-up SMS after</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'calls' && <CallLog extension={extension} />}
        {activeTab === 'voicemail' && <VoicemailList extension={extension} />}
        {activeTab === 'speed-dials' && <SpeedDials />}
        {activeTab === 'settings' && <VoipSettings extension={extension} onUpdate={loadExtension} />}
      </div>

      {/* Quick Intake Modal */}
      <QuickIntakeModal
        isOpen={showQuickIntakeModal}
        onClose={() => {
          setShowQuickIntakeModal(false);
          setIntakePreFill(null);
        }}
        onSuccess={() => {
          setShowQuickIntakeModal(false);
          setIntakePreFill(null);
        }}
        preFill={intakePreFill}
      />

      {/* Call Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-virgo-navy-700 rounded-xl shadow-xl max-w-xl w-full">
            <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <StickyNote className="w-5 h-5 text-amber-400" />
                Call Notes
              </h2>
              <button
                onClick={() => setShowNotesModal(false)}
                className="p-1 hover:bg-virgo-navy-600 rounded"
              >
                <X className="w-5 h-5 text-virgo-teal-400" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Timestamp Info */}
              <div className="bg-virgo-navy-800 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-virgo-teal-400">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{formatDate(new Date(), { weekday: 'short' })}</span>
                  </div>
                  <div className="flex items-center gap-2 text-virgo-teal-400">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{formatTime(new Date())}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-amber-400 font-mono">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Duration: {Math.floor(callDuration / 60)}:{(callDuration % 60).toString().padStart(2, '0')}</span>
                </div>
              </div>

              {/* File Under Case */}
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-2 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  File Under Case
                </label>
                <div className="relative">
                  <select
                    value={callNoteCase}
                    onChange={(e) => setCallNoteCase(e.target.value)}
                    className="w-full px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none"
                  >
                    <option value="">-- No case selected --</option>
                    {(Array.isArray(availableCases) ? availableCases : []).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.case_number || c.id} - {c.case_name || c.title || 'Untitled'}
                      </option>
                    ))}
                  </select>
                  <Briefcase className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-virgo-teal-500 pointer-events-none" />
                </div>
                <p className="text-xs text-virgo-teal-500 mt-1">
                  Select a case to file this call note under for record keeping
                </p>
              </div>

              {/* Billable Checkbox */}
              <div className="flex items-center gap-3 p-3 bg-virgo-navy-800 rounded-xl border border-virgo-navy-600">
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={callNoteBillable}
                    onChange={(e) => setCallNoteBillable(e.target.checked)}
                    className="w-5 h-5 text-amber-500 bg-virgo-navy-600 border-virgo-navy-500 rounded focus:ring-amber-500 focus:ring-offset-virgo-navy-800"
                  />
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    <span className="text-white font-medium">Billable Call</span>
                  </div>
                </label>
                {callNoteBillable && (
                  <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                    Will be added to time entries
                  </span>
                )}
              </div>

              {/* Notes Textarea */}
              <div>
                <label className="block text-sm text-virgo-teal-400 mb-2">
                  Notes
                </label>
                <textarea
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  placeholder="Type notes about this call..."
                  className="w-full h-36 px-4 py-3 bg-virgo-navy-800 border border-virgo-navy-600 rounded-xl text-white placeholder-virgo-navy-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-between items-center gap-3 p-4 border-t border-virgo-navy-600">
              <p className="text-xs text-virgo-teal-500">
                {callNoteCase ? 'Note will be filed under selected case' : 'Note will be saved to call log only'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="px-4 py-2 text-virgo-teal-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Save & File Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {showMessagesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-virgo-navy-700 rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-virgo-navy-600">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-400" />
                Messages
              </h2>
              <button
                onClick={() => setShowMessagesModal(false)}
                className="p-1 hover:bg-virgo-navy-600 rounded"
              >
                <X className="w-5 h-5 text-virgo-teal-400" />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">SMS Messaging</h3>
              <p className="text-virgo-teal-400 text-sm mb-4">
                Send and receive text messages with clients directly from here.
              </p>
              <p className="text-xs text-virgo-teal-500">
                Feature coming soon - will integrate with RingCentral SMS API
              </p>
            </div>
            <div className="flex justify-end p-4 border-t border-virgo-navy-600">
              <button
                onClick={() => setShowMessagesModal(false)}
                className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-400 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap with error boundary to prevent blank screens
const VoipPageWithErrorBoundary = () => (
  <VoipErrorBoundary>
    <VoipPage />
  </VoipErrorBoundary>
);

export default VoipPageWithErrorBoundary;
